import React, { MouseEventHandler, useMemo } from 'react';
import { useHideOverflowing } from 'shared/hooks/useHideOverflowing';
import { useTranslation } from 'react-i18next';
import {
  Connection,
  ConnectionType,
  Organization,
} from 'shared/types/ObjectTypes';
import Pagination from 'shared/components/ui/Pagination';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import {
  listPageClasses,
  buttonClasses,
  filterClasses,
  paginationClasses,
  skeletonClasses,
} from 'styles/tailwindClassnames';
import {
  CopyToClipboard,
  RefreshSpinner,
} from 'shared/components/ui/ProjectIcons';
import NoResults from 'shared/components/ui/NoResults';
import SelectInput from 'shared/components/inputs/SelectInput';
import SortByDropdown, {
  SortOption,
} from 'shared/components/inputs/SortByDropdown';
import { toast } from 'react-toastify';

type ConnectionsListProps = {
  canAddConnection: boolean;
  roleFilterValue: { label: string; value: string } | null;
  onChangeRole: (selected: { label: string; value: string } | null) => void;
  roleFilterDisabled?: boolean;
  reloadConnections: boolean;
  connections: Connection[];
  connectionTypes: ConnectionType[];
  loadingConnectionTypes: boolean;
  connectionUrls: string[];
  loadingConnectionUrls: boolean;
  organizations: Organization[];
  loadingOrganizations: boolean;
  onAddConnection: MouseEventHandler;
  onViewDetails: Function;
  onChangeGlobalSearch: (value: string) => void;
  onRefresh: () => void;
  globalSearchStr: string;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  onChangeEndpoint: Function;
  endpointFilterValue: any;
  onChangeType: Function;
  typeFilterValue: any;
  onChangeOwner: Function;
  ownerFilterValue: any;
  onPageClicked: Function;
  totalCount: number;
  pageLoaded: boolean;
  take: number;
  offset: number;
};

const ConnectionsList = ({
  canAddConnection,
  roleFilterValue,
  onChangeRole,
  roleFilterDisabled,
  reloadConnections,
  connections,
  connectionTypes,
  loadingConnectionTypes,
  connectionUrls,
  loadingConnectionUrls,
  organizations,
  loadingOrganizations,
  onAddConnection,
  onViewDetails,
  onChangeGlobalSearch,
  onRefresh,
  globalSearchStr,
  ordering,
  onChangeSortBy,
  onChangeEndpoint,
  endpointFilterValue,
  onChangeType,
  typeFilterValue,
  onChangeOwner,
  ownerFilterValue,
  onPageClicked,
  totalCount,
  pageLoaded,
  take,
  offset,
}: ConnectionsListProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  const { t } = useTranslation();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('Name (A-Z)'), value: 'name' },
      { label: t('Name (Z-A)'), value: '-name' },
      { label: t('Workspace'), value: 'owner__name' },
      { label: t('Technology'), value: 'type__name' },
    ],
    [t],
  );

  const hasActiveFilters = !!(
    globalSearchStr ||
    endpointFilterValue ||
    typeFilterValue ||
    ownerFilterValue
  );

  const clearAllFilters = () => {
    onChangeGlobalSearch('');
    onChangeEndpoint(null);
    onChangeType(null);
    onChangeOwner(null);
  };

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1
            data-testid="destinations_page_header"
            className={listPageClasses.title}
          >
            {t('Connections')}
          </h1>
          {canAddConnection && (
            <button className={buttonClasses.primary} onClick={onAddConnection}>
              {t('Create Connection')}
            </button>
          )}
        </div>
      </div>
      <div className={listPageClasses.body}>
        <div className="mx-auto max-w-list-table">
          {/* Search row */}
          {/* Never wraps: on a phone what does not fit is clipped, not dropped to its own line. */}
          <div
            ref={searchRowRef}
            className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip"
          >
            <GlobalSearch
              onChangeGlobalSearch={onChangeGlobalSearch}
              placeholder={t('Search')}
              savedString={globalSearchStr}
            />
            <SortByDropdown
              options={sortOptions}
              ordering={ordering}
              onChangeSortBy={onChangeSortBy}
            />
            <button
              type="button"
              onClick={() => onRefresh()}
              aria-label={t('Refresh list')}
              disabled={reloadConnections}
              className={`mb-2 ml-auto flex h-9 w-8 shrink-0 cursor-pointer items-center justify-center self-end ${reloadConnections ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
            >
              <RefreshSpinner />
            </button>
            {hasActiveFilters && (
              <button
                className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
                onClick={clearAllFilters}
              >
                {t('Clear All Filters')}
              </button>
            )}
          </div>
          {/* Filter dropdowns row */}
          <div
            ref={filterRowRef}
            className="mb-3 flex flex-row flex-nowrap gap-3 overflow-x-clip"
          >
            {/* Technology */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Technology')}</span>
              <SelectInput
                name="type__in"
                options={connectionTypes.map((d) => ({
                  label: d.name,
                  value: d.id,
                }))}
                value={typeFilterValue}
                onChange={onChangeType}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingConnectionTypes}
                className="w-full"
                variant="routeFilter"
              />
            </div>
            {/* URL */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('URL')}</span>
              <SelectInput
                name="base_url__in"
                options={connectionUrls.map((url) => {
                  let hostname = url;
                  try {
                    hostname = new URL(url).hostname;
                  } catch {}
                  return { label: hostname, value: url };
                })}
                value={endpointFilterValue}
                onChange={onChangeEndpoint}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingConnectionUrls}
                className="w-full"
                variant="routeFilter"
              />
            </div>
            {/* Role — derived from the Routes list, filtered in the browser */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Role')}</span>
              <SelectInput
                name="role"
                options={[
                  { label: t('Used as a source'), value: 'source' },
                  { label: t('Used as a destination'), value: 'destination' },
                ]}
                value={roleFilterValue}
                onChange={onChangeRole}
                isMulti={false}
                isClearable={true}
                isSearchable={false}
                isDisabled={!!roleFilterDisabled}
                isLoading={!!roleFilterDisabled}
                className="w-full"
                variant="routeFilter"
              />
            </div>
            {/* Workspace */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Workspace')}</span>
              <SelectInput
                name="owner__in"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                value={ownerFilterValue}
                onChange={onChangeOwner}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingOrganizations}
                className="w-full"
                variant="routeFilter"
              />
            </div>
          </div>
          {/* Cards */}
          <div className="flex w-full flex-col gap-2">
            {!pageLoaded || reloadConnections ? (
              [...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-teal-t30 bg-white p-4"
                  data-testid="loading"
                >
                  {/* Title row: name */}
                  <div className={`mb-3 h-5 w-48 ${skeletonClasses.base}`} />
                  {/* Content row */}
                  <div className="flex flex-col gap-6 min-[780px]:flex-row min-[780px]:items-start min-[780px]:gap-12">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                      <div className={`h-3 w-40 ${skeletonClasses.base}`} />
                    </div>
                    <div
                      className={`h-4 w-28 min-[780px]:ml-auto min-[780px]:mr-8 ${skeletonClasses.base}`}
                    />
                  </div>
                </div>
              ))
            ) : connections.length === 0 ? (
              <div className="rounded-lg border border-teal-t30 bg-white py-16">
                <NoResults
                  headerMsg={t('No Results Found')}
                  labelMsg={t(
                    'You may need to adjust your search or filters to find what you are looking for',
                  )}
                />
              </div>
            ) : (
              connections.map((connection: Connection) => {
                let dest_url: string;
                try {
                  dest_url = new URL(connection.base_url).hostname;
                } catch (err) {
                  dest_url = '';
                }
                const navigateToConnection = (e: React.MouseEvent) => {
                  if (e.metaKey || e.ctrlKey) {
                    window.open(
                      `/connections/${connection.id}/configuration`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  } else {
                    onViewDetails(connection.id);
                  }
                };
                return (
                  <div
                    key={connection.id}
                    className="cursor-pointer rounded-lg border border-teal-t30 bg-white p-4"
                    role="button"
                    tabIndex={0}
                    onClick={navigateToConnection}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onViewDetails(connection.id);
                      }
                    }}
                  >
                    {/* Title row: system name */}
                    <div className="mb-3">
                      <span
                        className="block truncate text-body-lg font-bold text-ui-cta-primary"
                        title={connection.name}
                      >
                        {connection.name}
                      </span>
                    </div>
                    {/* Content row: technology + url | workspace.
                                    NOTE for reviewers: the wide gap-12 here is intentional (gives the
                                    right-aligned workspace breathing room) and deliberately differs from
                                    the routes-list card spacing — not an inconsistency to flag. */}
                    <div className="flex flex-col gap-6 min-[780px]:flex-row min-[780px]:items-start min-[780px]:gap-12">
                      {/* Technology + URL */}
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-body-md font-normal text-ui-text-primary-text">
                          {connection.type.name}
                        </span>
                        {connection.base_url && (
                          <span className="flex min-w-0 items-center gap-2 text-body-sm text-ui-cta-primary">
                            <span
                              className="truncate"
                              title={connection.base_url}
                            >
                              {dest_url}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  connection.base_url,
                                );
                                toast.info(
                                  `${t('URL')} ${t('copied to clipboard')}`,
                                  {
                                    position: toast.POSITION.TOP_RIGHT,
                                    autoClose: 2000,
                                  },
                                );
                              }}
                              className="flex flex-shrink-0 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                              aria-label={t('Copy URL to clipboard')}
                            >
                              <CopyToClipboard />
                            </button>
                          </span>
                        )}
                      </div>
                      {/* Workspace */}
                      <div className="min-w-0 flex-shrink-0 min-[780px]:mr-8 min-[780px]:max-w-[50%] min-[780px]:text-right">
                        <span
                          className="block truncate text-body-sm text-ui-text-primary-text"
                          title={connection.owner.name}
                        >
                          {connection.owner.name}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {totalCount > 0 && (
              <Pagination
                showText={true}
                handlePageClick={onPageClicked}
                pageCount={Math.ceil(totalCount / take)}
                resultStart={offset + 1}
                resultEnd={
                  offset + take < totalCount ? offset + take : totalCount
                }
                totalCount={totalCount}
                initialPage={Math.floor(offset / take)}
                take={take}
                loading={!pageLoaded}
                mainclassname={paginationClasses.container}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionsList;
